import { prettyPrintJson } from 'pretty-print-json';

import {
  FILTER_OPTION_DISPLAY_TYPE,
  FILTER_OPTION_TYPE,
  SHOW_MORE_TYPES,
  VIEW_MORE_CLICK_DEBOUNCE_TIME,
} from '../constants/filter-tree';
import { getFilterSettings } from '../filter-tree';
import { renderingOptionBoxTemplate } from '../filter-tree/option-box';
import { renderingOptionListTemplate } from '../filter-tree/option-list';
import { renderingOptionSwatchTemplate } from '../filter-tree/option-swatch';
import { appIntegration } from '../integration';
import { formatCurrency, handleFirstLoadAfterRender, sortByKey, translateByKey } from './index';

const debounceTime =
  window.boostWidgetIntegrationConfig?.viewMoreClickDebounceTime || VIEW_MORE_CLICK_DEBOUNCE_TIME;

/**
 * @type {import('./index.d').watchFirstLoad}
 */
export function watchFirstLoad(context) {
  // Render filterTree base on latestFilterSearchRequest
  const newId = window.boostWidgetIntegration.generateUuid();

  if (context.blockType === 'filter') {
    // Response Filter API return before FeLib - Have filterTree
    if (context.filterTree) {
      context.state.filterTree = context.filterTree;
      context.state.latestFilterSearchRequest = newId;
      appIntegration();
      handleFirstLoadAfterRender(context);
    } else {
      // Response Filter API return after FeLib - Have not filterTree - observer WrapperDom of Filter
      const observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          if (context.filterTree) {
            context.state.filterTree = context.filterTree;
            observer.disconnect();
            context.state.latestFilterSearchRequest = newId;

            context.onFirstRender = function () {
              appIntegration();
              handleFirstLoadAfterRender(context);
            };
          }
        });
      });
      var config = { attributes: true, childList: true, subtree: true };
      observer.observe(context.document, config);
    }
  }
}

/**
 * @type {import('./index.d').debounce}
 */
export function debounce(func, delay) {
  let timeoutId;

  return function () {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const context = this;
    const args = arguments;

    clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      func.apply(context, args);
    }, delay);
  };
}

/**
 * @type {import('./index.d').debug}
 */
export function debug(context) {
  const debugDiv = document.getElementById('widgetIntegrationDebugDiv');
  if (debugDiv) {
    const widgetDebugCol1 = document.getElementById('widgetDebugCol1');
    const widgetDebugCol2 = document.getElementById('widgetDebugCol2');
    const widgetDebugCol3 = document.getElementById('widgetDebugCol3');
    const filterTreeOptions = context.state?.filterTree?.options?.map((op) => {
      return {
        filterType: op.filterType,
        filterOptionId: op.filterOptionId,
        position: op.position,
        displayType: op.displayType,
        count: op?.values?.length || 0,
      };
    });
    widgetDebugCol1.innerHTML = `
    <h3>filterTree: </h3>
    <pre id=account class=json-container>${prettyPrintJson.toHtml(filterTreeOptions)}</pre>
    `;

    //   '<h3>filterTree: </h3>' +
    //   context.state?.filterTree?.options
    //     ?.map((option) => {
    //       return `<p> ${option.filterOptionId} - ${
    //         option.values && option.values.length ? `total ${option.values.length}` : ''
    //       } ${
    //         option.values && !option.values.length && typeof option.values === 'object'
    //           ? JSON.stringify(option.values, null, 2)
    //           : ''
    //       }
    //       ${option.sliderStep ? `sliderStep: ${option.sliderStep}` : ''}
    //       ${option.sliderRange ? `sliderRange: ${option.sliderRange}` : ''}
    //       </p>`;
    //     })
    //     .join('');
    // const filterTreeViewPort =
    //   context.filterTreeViewPort &&
    //   typeof context.filterTreeViewPort === 'object' &&
    //   Object.keys(context.filterTreeViewPort)
    //     .map((option) => {
    //       if (option !== 'key')
    //         return `<p>${option}: displayType
    //         ${context.filterTreeViewPort[option].displayType} loaded: ${context.filterTreeViewPort[option].loaded} - total: ${context.filterTreeViewPort[option].total}
    //         </p>`;
    //     })
    //     .join('');
    widgetDebugCol2.innerHTML = `
    <h3>filterTreeViewPort: </h3>
    <pre id=account class=json-container>${prettyPrintJson.toHtml(context.filterTreeViewPort)}</pre>
    `;

    // '<h3>filterTreeViewPort: </h3> ' + filterTreeViewPort;

    widgetDebugCol3.innerHTML = `
    <h3>Filter: </h3>
    <pre id=account class=json-container>${prettyPrintJson.toHtml(context.state.filter)}</pre>
    <pre id=account class=json-container>${prettyPrintJson.toHtml(context.updatedParams)}</pre>

    `;

    // '<h3>Filter: </h3> ' + filter;
  }
}

/**
 * @type {import('./index.d').formatPercentSaleLabel}
 */
export const formatPercentSaleLabel = (context, from = 0, to) => {
  let label = '';
  if (!from) {
    label = context.translate('under', 'Under') + ` ${to}%`;
  } else if (!to) {
    label = context.translate('above', 'Above') + ` ${from}%`;
  } else {
    label = `${from}% - ${to}%`;
  }

  return label;
};

/**
 * @type {import('./index.d').roundAmount}
 */
export const roundAmount = (amount, precision = 0, isMinPrice = false) => {
  if (isNaN(amount)) return amount;
  amount = Number(amount);

  if (amount < 0.04) return amount;

  if (isMinPrice) return Math.floor(amount * 100) / 100;

  return +amount.toFixed(precision);
};

/**
 * @type {import('./index.d').moneyFormatPriceList}
 */
export const moneyFormatPriceList = (context, number, precisionFormatLabelPriceList = 2) => {
  const amount = roundAmount(number, precisionFormatLabelPriceList);
  return formatCurrency({ context, value: amount });
};

/**
 * @type {import('./index.d').formatPriceLabel}
 */
export const formatPriceLabel = (context, to, from = 0, precisionFormatLabelPriceList = 2) => {
  let label = '';
  if (!from || from === '0') {
    label =
      context.translate('under', 'Under') +
      ` ${moneyFormatPriceList(context, to, precisionFormatLabelPriceList)}`;
  } else if (!to) {
    label =
      context.translate('above', 'Above') +
      ` ${moneyFormatPriceList(context, from, precisionFormatLabelPriceList)}`;
  } else {
    label = `${moneyFormatPriceList(
      context,
      from,
      precisionFormatLabelPriceList
    )} - ${moneyFormatPriceList(context, to, precisionFormatLabelPriceList)}`;
  }

  return label;
};

/**
 * @type {import('./index.d').formatFromToKey}
 */
export const formatFromToKey = (from = 0, to = 0) => {
  let key = '';
  if (!from) {
    key = `0:${to}`;
  } else if (!to) {
    key = `${from}:`;
  } else {
    key = `${from}:${to}`;
  }

  return key;
};

/**
 * @type {import('./index.d').formatRatingValue}
 */
export const formatRatingValue = (context, from, showExactRating) => {
  let label = from.toFixed();
  if (from === 1) {
    label += ` ${translateByKey(context, 'ratingStar', 'star')}`;
  } else {
    label += ` ${translateByKey(context, 'ratingStars', 'stars')}`;
  }

  if (!showExactRating) label += ` ${translateByKey(context, 'ratingUp', '& Up')}`;

  return label;
};

const isDefaultSort = (option) => {
  const alwaySortFilterType = [FILTER_OPTION_TYPE.REVIEW_RATINGS, FILTER_OPTION_TYPE.PERCENT_SALE];

  if (alwaySortFilterType.includes(option.filterType)) return true;

  const excludeFilterType = [FILTER_OPTION_TYPE.STOCK];
  const excludeDisplayType = [
    FILTER_OPTION_DISPLAY_TYPE.RANGE,
    FILTER_OPTION_DISPLAY_TYPE.MULTI_LEVEL_COLLECTIONS,
  ];

  const isSortAll = option.valueType === 'all';
  const isSortManual = option.valueType !== 'all' && option.sortManualValues;
  const isTextRangeSlider =
    option.displayType === FILTER_OPTION_DISPLAY_TYPE.RANGE && option.isNumberRangeSlider === false;

  return (
    !excludeFilterType.includes(option.filterType) &&
    (!excludeDisplayType.includes(option.displayType) || isTextRangeSlider) &&
    (isSortAll || isSortManual)
  );
};

/**
 * @type {import('./index.d').replacePrefixValue}
 */
export function replacePrefixValue(option, value) {
  let replacedLabel = value?.label;

  if (option?.prefix) {
    const prefix = option?.prefix?.replace(/\\/g, '') || '';
    if (!replacedLabel) replacedLabel = value?.key || value?.tag || '';
    replacedLabel = replacedLabel.replace(prefix, '').trim();
  }

  return replacedLabel;
}

/**
 * @type {import('./index.d').updateValuesOptions}
 */
export const updateValuesOptions = (options, settings = {}, context) => {
  if (!Array.isArray(options)) return options;
  const { hideSingleOption = false, showOutOfStockOption = false } = settings;

  return options.map((option) => {
    // Option Values is array
    if (Array.isArray(option.values)) {
      const isMultiLevel =
        option.displayType === 'multi_level_collections' || option.filterType === 'multi_level_tag';

      // filter doc_count valid when showOutOfStockOption = false and option.keepValuesStatic = false
      if (!showOutOfStockOption && !option.keepValuesStatic) {
        option.values = isMultiLevel
          ? filterMultiLevelOptionsByDocCount(option.values)
          : filterOptionsByDocCount(option.values);
      }

      //Create labels in values if not exists
      const labelAvailable = option.values.some((value) => 'label' in value);
      if (!labelAvailable) {
        option.values.forEach((value) => {
          if (value.key) value.label = value.key;
        });
      }

      /** Hide filter option when filter settings hideSingleOption = true and length values = 1 **/
      if (hideSingleOption && option.values.length === 1) {
        const onlyNode = option.values[0];
        if (isMultiLevel) {
          if (!hasNestedChildren(onlyNode)) {
            option.values = [];
          }
        } else {
          option.values = [];
        }
      }
    }

    if (option?.values?.length === 0) {
      const filterOption = document.getElementById(
        `${option.filterOptionId.replace(/::/g, '__')}-toggle`
      );

      filterOption && filterOption?.closest('.boost-sd__filter-option')?.remove(); // remove filter option from server render first.
    }

    // if API return not value sortType then check and assign default value sortType if this Option valid
    if (!option.sortType) {
      option.sortType = isDefaultSort(option) ? 'key-asc' : '';
    }

    const isManualValues = option.manualValues?.length > 0;
    /* filter option settings - advanced Sort type
     * sort <=> isManualValues & sortManualValues or allValues(!isManualValues)
     */
    if (
      ((option.sortManualValues && isManualValues) || !isManualValues) &&
      Array.isArray(option.values) &&
      option.sortType
    ) {
      const [key, order] = option.sortType?.split('-') || [];
      if (key && order) {
        let _key = key;
        if (option.filterType === 'collection' && key === 'key') {
          _key = 'displayName';
        }

        option.values = sortByKey(option.values, _key, order);
      }
    }

    if ((option.filterType === 'review_ratings' || option.displayType === 'rating') && context) {
      // only apply for rating
      option.values = option.values?.map((item) => {
        item.key = Math.round(item.from);
        item.label = formatRatingValue(context, item.from, option.showExactRating);
        return item;
      });
    }

    // some filterType not have selectType but default multiple selectType
    if (
      option.displayType === 'list' &&
      ['stock', 'percent_sale', 'price', 'variants_price'].includes(option.filterType)
    ) {
      option.selectType = 'multiple';
    }

    return option;
  });
};

/**
 * Checks if the given term is a bad search term containing potential XSS vulnerabilities.
 * @type {import('./index.d').isBadSearchTerm}
 */
export const isBadSearchTerm = (term) => {
  if (typeof term == 'string') {
    term = term.toLowerCase();
    // List of known DOM events and other potential XSS indicators
    var domEvents = [
      'script',
      'alert',
      'onabort',
      'popstate',
      'afterprint',
      'beforeprint',
      'beforeunload',
      'blur',
      'canplay',
      'canplaythrough',
      'change',
      'click',
      'contextmenu',
      'copy',
      'cut',
      'dblclick',
      'drag',
      'dragend',
      'dragenter',
      'dragleave',
      'dragover',
      'dragstart',
      'drop',
      'durationchange',
      'ended',
      'error',
      'focus',
      'focusin',
      'focusout',
      'fullscreenchange',
      'fullscreenerror',
      'hashchange',
      'input',
      'invalid',
      'keydown',
      'keypress',
      'keyup',
      'load',
      'loadeddata',
      'loadedmetadata',
      'loadstart',
      'mousedown',
      'mouseenter',
      'mouseleave',
      'mousemove',
      'mouseover',
      'mouseout',
      'mouseout',
      'mouseup',
      'offline',
      'online',
      'pagehide',
      'pageshow',
      'paste',
      'pause',
      'play',
      'playing',
      'progress',
      'ratechange',
      'resize',
      'reset',
      'scroll',
      'search',
      'seeked',
      'seeking',
      'select',
      'show',
      'stalled',
      'submit',
      'suspend',
      'timeupdate',
      'toggle',
      'touchcancel',
      'touchend',
      'touchmove',
      'touchstart',
      'unload',
      'volumechange',
      'waiting',
      'wheel',
      'load',
    ];
    const potentialEventRegex = new RegExp(domEvents.join('=|on'));
    // Detect actual JavaScript function calls
    const isAlert = (term.match(/alert\(/g) || []).length;
    const isConsoleLog = (term.match(/console\.log\(/g) || []).length;
    const isExecCommand = (term.match(/execCommand/g) || []).length;
    const isCookie = (term.match(/document\.cookie/g) || []).length;
    const isJavascript = (term.match(/j.*a.*v.*a.*s.*c.*r.*i.*p.*t/g) || []).length;

    // Detect HTML tags (more specific - must have tag name after < or before >)
    const hasHtmlTags = /<\w+|<\/\w+|\w+>/g.test(term);
    const isMetaTag = (term.match(/<meta/gi) || []).length;
    const isPotentialEvent = potentialEventRegex.test(term);

    // XSS-specific patterns (not just any < or >)
    const xssRegex =
      /\x3Cscript|<script|<\/script|<img[\s\S]*?src|<iframe|<\/iframe|<object|<embed|<applet|on\w+\s*=|javascript:|data:text\/html|&#[xX][0-9a-fA-F]+;/gi;
    const isXssTerm = xssRegex.test(term);

    if (
      hasHtmlTags ||
      isAlert ||
      isConsoleLog ||
      isExecCommand ||
      isCookie ||
      isJavascript ||
      isPotentialEvent ||
      isMetaTag ||
      isXssTerm
    ) {
      return true;
    }
  }
  return false;
};

/**
 * Filter XSS from options in the filter tree.
 * @type {import('./index.d').xssFilterOption}
 */
export function xssFilterOption(option, size) {
  const ops = { ...option };
  const values = [...ops.values];
  const newValues = [];
  for (const value of values) {
    if (
      !isBadSearchTerm(value?.key) &&
      !isBadSearchTerm(value?.label) &&
      !isBadSearchTerm(value?.displayName)
    )
      newValues.push(value);
    if (newValues.length >= size) break;
  }
  return newValues;
}

/**
 * Sanitize filter label to prevent XSS attacks.
 *
 * Security approach:
 * - Detects malicious content using isBadSearchTerm()
 * - BLOCKS dangerous content completely (returns safe placeholder)
 * - For safe content, returns original (no escaping needed for textContent/setAttribute)
 *
 * Why we block instead of escape:
 * - Even though textContent is XSS-safe, displaying JavaScript code like "alert('xss')"
 *   is still a security concern per security audits
 * - Could be exploited via copy-paste, social engineering, or accessibility APIs
 * - Better UX: Show "[Blocked Content]" than confusing JavaScript syntax
 *
 * @param {string} label - The filter label to sanitize
 * @returns {string} The original label if safe, or "[Blocked Content]" if malicious/invalid
 * @type {import('./index.d').sanitizeFilterLabel}
 */
export function sanitizeFilterLabel(label) {
  if (!label || typeof label !== 'string') {
    return '[Invalid Label]';
  }

  // Check for malicious content (XSS patterns, JavaScript code, etc.)
  if (isBadSearchTerm(label)) {
    // BLOCK malicious content - don't display JavaScript code even as text
    return '[Blocked Content]';
  }

  // Safe content - return as-is (textContent/setAttribute handle safely)
  return label;
}

/**
 * Returns a limited number of values from the option object.
 * @type {import('./index.d').getLimitOptionValues}
 */
export function getLimitOptionValues(option, size) {
  // Check if option.values is an array and size is a valid positive number
  if (Array.isArray(option.values) && typeof size === 'number' && size > 0) {
    // Use slice to return a sub-array containing a maximum of 'size' elements
    return option.values.slice(0, size);
  }
  // Return an empty array if the input is invalid
  return [];
}

/**
 * Checks if the given option is a view more filter option.
 * @type {import('./index.d').isViewMoreFilterOption}
 */
export const isViewMoreFilterOption = (option) => {
  return (
    !!option.showMoreType &&
    [SHOW_MORE_TYPES.VIEW_MORE, SHOW_MORE_TYPES.VIEW_MORE_SCROLL_BAR].includes(option.showMoreType)
  );
};

/**
 * Returns the number of items to view more based on the option's display type and filter tree orientation.
 * @type {import('./index.d').getNumberViewMore}
 */
export const getNumberViewMore = (option, isFilterTreeVertical) => {
  const _viewMoreNumber = isFilterTreeVertical
    ? window.boostWidgetIntegrationConfig?.startViewMore || { list: 5, box: 3, swatch: 10 }
    : window.boostWidgetIntegrationConfig?.startViewMore || { list: 10, box: 20, swatch: 10 };

  switch (option.displayType) {
    case FILTER_OPTION_DISPLAY_TYPE.LIST:
      return _viewMoreNumber.list;

    case FILTER_OPTION_DISPLAY_TYPE.BOX:
      return _viewMoreNumber.box;

    case FILTER_OPTION_DISPLAY_TYPE.SWATCH:
      return _viewMoreNumber.swatch;
    default:
      return _viewMoreNumber.list;
  }
};

/**
 * Manages the state of the view more feature.
 * @type {import('./index.d').manageFilterTreeViewMoreState}
 */
export const manageFilterTreeViewMoreState = async ({
  context,
  label,
  values,
  swatchShape,
  swatchType,
  option,
  dom,
  showDocCount,
  selectedKeys,
  classNameSelected,
  classNameUnselected,
  actionMapping,
  setActionMapping,
}) => {
  const [getState, setState] = context.useContextState(`filter-tree-view-more`, {});
  const [getFilterTypeAndDisplayType, setFilterTypeAndDisplayType] = context.useContextState(
    `filter-tree-filter-type-and-display-type`,
    {}
  );

  const isFilterTreeVertical = getFilterSettings(context)?.filterLayout === 'vertical';
  const numberViewMore = getNumberViewMore(option, isFilterTreeVertical);
  const viewMore = getState();
  const filterOptionContentInner = dom.closest('.boost-sd__filter-option-content-inner');
  const viewMoreBtnDom = filterOptionContentInner.nextElementSibling;

  viewMore[option.filterOptionId] = {
    label,
    values,
    numberViewMore,
    enabled: isViewMoreFilterOption(option) && values.length > numberViewMore,
    isExpanded: viewMore[option.filterOptionId]?.isExpanded || false,
    showMoreType: option.showMoreType,
    dom,
    viewMoreBtnDom,
    option,
    swatchShape,
    swatchType,
    showDocCount,
    selectedKeys,
    classNameSelected,
    classNameUnselected,
    actionMapping,
    setActionMapping,
    lastClickedTime: Date.now(),
  };

  const onViewMoreClick = (e) => {
    const viewMoreState = getState();
    const filterOptionState = viewMoreState[option.filterOptionId];
    const now = Date.now();

    // Avoid multiple clicks
    if (now - filterOptionState.lastClickedTime > debounceTime) {
      filterOptionState.isExpanded = !filterOptionState.isExpanded;
      filterOptionState.values = option.values;
      filterOptionState.lastClickedTime = now;

      setState(viewMoreState);
      setFilterTypeAndDisplayType({
        filterOptionId: option.filterOptionId,
        displayType: option.displayType,
      });
    }
  };

  viewMoreBtnDom?.addEventListener('click', onViewMoreClick);

  /**
   * re-render when a specific filter option state changed
   * @example  When clicked 'View more' and triggered onViewMoreClick()
   */
  context.render(async () => {
    const { filterOptionId, displayType } = getFilterTypeAndDisplayType();
    const renderingTemplateFunction = getRenderingTemplateFunction(displayType);

    if (!filterOptionId || !displayType || !renderingTemplateFunction) return;

    await renderingWithShowMoreTypeAsViewMore({
      context,
      filterOptionId,
      filterOptionDisplayType: displayType,
      renderingTemplateFunction,
    });
  }, ['filter-tree-filter-type-and-display-type']);

  const renderingTemplateFunction = getRenderingTemplateFunction(option.displayType);

  // render when function is called
  await renderingWithShowMoreTypeAsViewMore({
    context,
    filterOptionId: option.filterOptionId,
    filterOptionDisplayType: option.displayType,
    renderingTemplateFunction,
  });
};

/**
 * Renders the view more feature for a filter tree view.
 * @type {import('./index.d').renderingWithShowMoreTypeAsViewMore}
 */
export const renderingWithShowMoreTypeAsViewMore = async ({
  context,
  filterOptionId,
  filterOptionDisplayType,
  renderingTemplateFunction,
}) => {
  const [getState] = context.useContextState('filter-tree-view-more');
  const viewState = getState();
  const updatedState = viewState[filterOptionId];

  const {
    isExpanded,
    viewMoreBtnDom,
    enabled,
    numberViewMore,
    option: { displayType },
  } = updatedState;

  let { values } = updatedState;

  if (displayType !== filterOptionDisplayType) return;

  if (enabled && viewMoreBtnDom) {
    viewMoreBtnDom.style.display = 'block';
    if (isExpanded) {
      viewMoreBtnDom.textContent = translateByKey(context, 'viewLess', 'View Less');
    } else {
      values = values.slice(0, numberViewMore);
      viewMoreBtnDom.textContent = translateByKey(context, 'viewMore', 'View More');
    }
  }

  await renderingTemplateFunction({
    ...updatedState,
    context,
    values,
  });
};

/**
 * Returns the rendering template function based on the display type.
 * @type {import('./index.d').renderingWithShowMoreTypeAsViewMore}
 */
const getRenderingTemplateFunction = (displayType) => {
  const templates = {
    [FILTER_OPTION_DISPLAY_TYPE.LIST]: renderingOptionListTemplate,
    [FILTER_OPTION_DISPLAY_TYPE.BOX]: renderingOptionBoxTemplate,
    [FILTER_OPTION_DISPLAY_TYPE.SWATCH]: renderingOptionSwatchTemplate,
  };

  return templates[displayType];
};

/**
 * @type {import('./index.d').filterOptionsByDocCount}
 */
export const filterOptionsByDocCount = (values) => {
  if (!Array.isArray(values)) return [];
  return values.filter(
    (v) => typeof v === 'object' && v !== null && !Array.isArray(v) && Number(v.doc_count) > 0
  );
};

/**
 * @type {import('./index.d').findChildKey}
 */
const findChildKey = (node) => {
  const CHILD_KEYS = ['tags', 'subTags', 'values'];
  return (
    CHILD_KEYS.find(
      (key) => Object.prototype.hasOwnProperty.call(node, key) && Array.isArray(node[key])
    ) || null
  );
};

/**
 * @type {import('./index.d').processNodeWithChildren}
 */
const processNodeWithChildren = (node, childKey) => {
  const filtered = filterMultiLevelOptionsByDocCount(node[childKey]);

  if (filtered.length > 0) {
    return node[childKey] === filtered ? node : { ...node, [childKey]: filtered };
  }

  if (Number(node.doc_count) > 0) {
    const { [childKey]: _, ...rest } = node;
    return rest;
  }

  return null;
};
/**
 * @type {import('./index.d').filterMultiLevelOptionsByDocCount}
 */
const filterMultiLevelOptionsByDocCount = (values) => {
  if (!Array.isArray(values)) return [];

  const out = [];

  for (const item of values) {
    if (typeof item !== 'object' || item === null || Array.isArray(item)) continue;

    const node = item;
    const childKey = findChildKey(node);

    if (childKey) {
      const processedNode = processNodeWithChildren(node, childKey);
      if (processedNode) {
        out.push(processedNode);
      }
      continue;
    }

    if (Number(node.doc_count) > 0) {
      out.push(node);
    }
  }

  return out;
};

/**
 * @type {import('./index.d').hasNestedChildren}
 */
const hasNestedChildren = (node) => {
  const childKeys = ['tags', 'subTags'];
  return childKeys.some((k) => Array.isArray(node?.[k]) && node[k].length > 0);
};
